// StarkNav — the shared top-nav for every stark page. Minimal by design: the four
// pillar pages plus the consult CTA. Kept deliberately spare so the mono nav reads
// clean; the fuller sitemap lives in StarkFooter. Active route is marked with .on.
//
// Every site carries a sign-in entry point, so the nav also shows Login when signed
// out. Signed in, the operator pages (Engagement Scout, Agent Composer) and Logout sit
// under a More pulldown, so the bar never wraps to a second line and no page needs a
// typed address. On narrow windows and phones the pillar links collapse behind a Menu
// button; its panel also carries the operator pages and Logout when signed in. Login
// stays visible beside Menu.
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { OPERATOR_NAV_LINKS } from "../config/site";
import { userPool } from "../awsConfig";
import { clearAuthState, isAuthenticated } from "../utils/authUtils";

const LINKS = [
  { to: "/platform", label: "Platform" },
  { to: "/agent-composer", label: "Agents" },
  { to: "/live", label: "Live" },
  { to: "/how-we-work", label: "How we work" },
  { to: "/proof", label: "Proof" },
  { to: "/lending-demo", label: "Demo" },
  { to: "/about", label: "About" },
];

function handleLogout(event) {
  event.preventDefault();
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) cognitoUser.signOut();
  clearAuthState();
  window.location.replace("/login");
}

export default function StarkNav() {
  const { pathname } = useLocation();
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const signedIn = isAuthenticated();
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  useEffect(() => {
    setMenuOpen(false);
    setMoreOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMoreOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const operatorLinks = (
    <>
      {OPERATOR_NAV_LINKS.map((l) =>
        l.external ? (
          <a key={l.path} href={l.path} target="_blank" rel="noopener noreferrer">{l.name}</a>
        ) : (
          <Link key={l.path} to={l.path} className={normalizedPathname === l.path ? "on" : undefined}>{l.name}</Link>
        )
      )}
      <a href="/login" onClick={handleLogout}>Logout</a>
    </>
  );

  return (
    <nav className="sk-nav">
      <Link to="/" className="sk-brand"><span className="sk-mark">X20</span> CONSULTING</Link>
      <div className="sk-navlinks">
        {LINKS.map((l) => (
          <Link key={l.to} to={l.to} className={normalizedPathname === l.to ? "on" : undefined}>{l.label}</Link>
        ))}
        {signedIn ? (
          <div className="sk-more" ref={moreRef}>
            <button
              type="button"
              className="sk-morebtn"
              aria-expanded={moreOpen}
              aria-controls="sk-moremenu"
              onClick={() => setMoreOpen((o) => !o)}
            >
              More <span aria-hidden="true">▾</span>
            </button>
            {moreOpen && (
              <div id="sk-moremenu" className="sk-moremenu">{operatorLinks}</div>
            )}
          </div>
        ) : (
          <Link to="/login" className="sk-navauth">Login</Link>
        )}
        <button
          type="button"
          className="sk-menubtn"
          aria-expanded={menuOpen}
          aria-controls="sk-navmenu"
          onClick={() => setMenuOpen((o) => !o)}
        >
          {menuOpen ? "Close" : "Menu"}
        </button>
      </div>
      {menuOpen && (
        <div id="sk-navmenu" className="sk-navmenu">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} className={normalizedPathname === l.to ? "on" : undefined}>{l.label}</Link>
          ))}
          {signedIn && operatorLinks}
        </div>
      )}
    </nav>
  );
}
